import React from 'react';
import {
  Box, Typography, Grid, CardMedia, useTheme, Button, Container, Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import PoolIcon from '@mui/icons-material/Pool';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PageHeader from '../ui/PageHeader';

const infoIcons = {
  pool: <PoolIcon fontSize="small" />,
  levels: <SchoolIcon fontSize="small" />,
  amenities: <CheckCircleOutlineIcon fontSize="small" />,
  schedule: <AccessTimeIcon fontSize="small" />,
};

const locations = [
  {
    title: 'Knox Aquatic Centre',
    image: 'images/SJ_Gallery/Knox_Pool.jpg',
    link: 'https://g.co/kgs/A3bg3sB',
    badge: 'All Levels',
    info: [
      { icon: 'pool',      text: 'LTS pool · 25m pool · 50m pool' },
      { icon: 'levels',    text: 'All levels - Learn to Swim through Squad' },
      { icon: 'amenities', text: 'Free parking · Parent viewing area · Changerooms & showers' },
      { icon: 'schedule',  text: 'See our Programs page for the current lesson timetable' },
    ],
  },
  {
    title: 'Aquabliss Pymble',
    image: 'images/SJ_Gallery/AQ_LTS_1.jpeg',
    link: 'https://g.co/kgs/JZqmw48',
    badge: 'Levels 1–4',
    info: [
      { icon: 'pool',      text: 'LTS small pool' },
      { icon: 'levels',    text: 'Levels 1–4 (Learn to Swim, beginners & early learners)' },
      { icon: 'amenities', text: 'Free parking · Parent viewing area · Changerooms & showers' },
      { icon: 'schedule',  text: 'See our Programs page for the current lesson timetable' },
    ],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

const Locations = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg">
        <PageHeader
          title="Our Locations"
          subtitle="Our swim club operates across two welcoming venues. Each offers a safe, supportive environment with qualified coaches and great facilities for families."
        />

        <Box sx={{ maxWidth: { xs: '100%', md: '85%' }, mx: 'auto' }}>
          <Grid container spacing={{ xs: 3, md: 5 }} justifyContent="center">
            {locations.map((location, index) => (
              <Grid item xs={12} sm={10} md={6} key={index}>
                <Box
                  component={motion.div}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ delay: index * 0.15 }}
                  sx={{
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0px 10px 40px rgba(0,0,0,0.5)',
                    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease',
                    background: theme.palette.background.paper,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 20px 50px rgba(0,0,0,0.7)',
                      '& .hover-image': { transform: 'scale(1.06)' },
                      '& .gradient-overlay': { opacity: 1 },
                      '& .overlay-text': { transform: 'translateY(0)' },
                    },
                  }}
                >
                  {/* Image Section */}
                  <Box
                    component="a"
                    href={location.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'block', textDecoration: 'none' }}
                  >
                    <Box sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      height: { xs: '200px', sm: '240px', md: '260px' },
                    }}>
                      <CardMedia
                        className="hover-image"
                        component="img"
                        height="100%"
                        image={location.image}
                        alt={location.title}
                        sx={{
                          objectFit: 'cover',
                          width: '100%',
                          transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        }}
                      />

                      {/* Levels badge */}
                      <Chip
                        label={location.badge}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 14,
                          right: 14,
                          zIndex: 3,
                          bgcolor: theme.palette.primary.main,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                        }}
                      />

                      <Box
                        className="gradient-overlay"
                        sx={{
                          position: 'absolute', bottom: 0, left: 0, right: 0, height: '80%',
                          background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
                          transition: 'opacity 0.4s ease',
                          opacity: 0.6, zIndex: 1,
                        }}
                      />
                      <Box
                        className="overlay-text"
                        sx={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          textAlign: 'center', py: { xs: 2, md: 2.5 }, px: 2, zIndex: 2,
                          transform: 'translateY(6px)', transition: 'transform 0.4s ease',
                        }}
                      >
                        <Typography variant="h5" sx={{
                          color: '#ffffff', fontWeight: 700,
                          textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                        }}>
                          {location.title}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Info Section */}
                  <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                    {location.info.map(({ icon, text }, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{ color: theme.palette.primary.main, mt: 0.25, flexShrink: 0 }}>
                          {infoIcons[icon]}
                        </Box>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                          {text}
                        </Typography>
                      </Box>
                    ))}

                    <Button
                      component="a"
                      href={location.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="medium"
                      endIcon={<OpenInNewIcon />}
                      sx={{
                        mt: 1, borderRadius: 2, textTransform: 'none',
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: 'rgba(204, 34, 41, 0.08)',
                          borderColor: theme.palette.primary.light,
                        },
                      }}
                    >
                      View on Maps
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Locations;
